import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { applyActionCode, getAuth } from "firebase/auth";
import {
  Box,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const auth = getAuth();

  useEffect(() => {
    const verifyEmail = async () => {
      const actionCode = searchParams.get("oobCode");

      if (!actionCode) {
        setVerificationState("error");
        setErrorMessage(
          "認証コードが見つかりません。URLが正しいか確認してください。",
        );
        return;
      }

      try {
        await applyActionCode(auth, actionCode);
        // Reload the user to refresh the auth state
        if (auth.currentUser) {
          await auth.currentUser.reload();
        }
        setVerificationState("success");
      } catch (error: any) {
        setVerificationState("error");
        let message = "メール認証に失敗しました。";

        if (error.code === "auth/invalid-action-code") {
          message =
            "無効な認証コードです。リンクが期限切れか、すでに使用されています。";
        } else if (error.code === "auth/user-disabled") {
          message = "このユーザーアカウントは無効化されています。";
        } else if (error.code === "auth/user-not-found") {
          message = "ユーザーが見つかりません。";
        }

        setErrorMessage(message);
        console.error("認証エラー:", error);
      }
    };

    verifyEmail();
  }, [auth, searchParams]);

  const handleGoToTop = () => {
    navigate("/mypage");
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        mt: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {verificationState === "loading" && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              メールアドレスを確認中...
            </Typography>
            <Typography variant="body1" align="center">
              しばらくお待ちください。メールアドレスの確認を処理しています。
            </Typography>
          </>
        )}

        {verificationState === "success" && (
          <>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 60, mb: 3 }}
            />
            <Typography variant="h5" component="h1" gutterBottom>
              メールアドレスの確認が完了しました！
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
              ご登録いただいたメールアドレスの確認が完了しました。
            </Typography>
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToTop}
              >
                ログイン
              </Button>
            </Box>
          </>
        )}

        {verificationState === "error" && (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 3 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              メールアドレスの確認に失敗しました
            </Typography>
            <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
              {errorMessage}
            </Alert>
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToTop}
              >
                ログイン
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
}
